import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Volume2Icon, VolumeXIcon } from 'lucide-react';
import { HouseModeChip } from '../ui/HouseModeChip';
import { useSpecSettings } from '../../contexts/SpecSettingsContext';
import { useDb } from '../../hooks/useDb';
import { rainNext2h, roomById, weatherNow } from '../../utils/dbSelectors';
import type { AnnaStage } from '../../hooks/useAnnaStage';
interface WallTopBarProps {
  stage: AnnaStage;
  /** Nothing going on: show only the time */
  calm: boolean;
}

/** Top edge, very quiet: time, weather + rain next 2 h, house mode, room, connection dots. */
export function WallTopBar({
  stage,
  calm
}: WallTopBarProps) {
  const {
    soundOn,
    setSoundOn
  } = useSpecSettings();
  const {
    meta,
    weather,
    rain,
    room
  } = useDb(t => ({
    meta: t.meta,
    weather: weatherNow(t),
    rain: rainNext2h(t),
    room: roomById(t, t.meta.roomId)
  }));
  const sleep = stage.view === 'sleep';
  const showDetails = !sleep && !calm;
  const maxRain = Math.max(1, ...rain.quarters);
  const down = (Object.entries(meta.connections) as [string, boolean][]).filter(([, ok]) => !ok);
  return <header className="pointer-events-none absolute inset-x-0 top-0 z-20 flex items-start justify-between gap-6 px-6 pt-14 lg:px-10" data-id="element-1768" data-mp="2cbc5wztjtynk">
      <div className="pointer-events-auto flex items-end gap-8" data-id="element-1769" data-mp="23d0gh14f56wp">
        {/* Dim clock in Slapen stays at 6:1 contrast — dimmed by colour, not opacity */}
        <time className={`font-display text-[56px] font-extralight leading-none tabular-nums lg:text-[72px] ${sleep ? 'text-ink-muted' : 'text-ink'}`} data-id="element-1770" data-mp="30285ij48xy5d">
          {meta.time}
        </time>
        <AnimatePresence data-id="element-1771" data-mp="2r0rwaep6422q">
          {showDetails && <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.3
        }} className="hidden pb-1 sm:block" data-id="element-1772" data-mp="2m3ro9y8rcp8z">
              <p className="text-[22px] text-ink-soft" data-id="element-1773" data-mp="1ami9vq4hknej">
                {weather.temp}° · {weather.sky}
              </p>
              <div className="mt-1 flex items-end gap-3" data-id="element-1774" data-mp="1c3fjfyag6gw1">
                <div className="flex h-6 items-end gap-[3px]" aria-hidden data-id="element-1775" data-mp="1h30is9tj4r9a">
                  {rain.quarters.map((mm, i) => <span key={i} className={`w-1.5 rounded-sm ${mm > 0 ? 'bg-[#8FB8D8]' : 'bg-white/15'}`} style={{
                height: `${Math.max(3, mm / maxRain * 24)}px`
              }} data-id="element-1776" data-mp="136n0iyh68ltr" data-mpi={i} />)}
                </div>
                <span className="text-[22px] text-ink-muted" data-id="element-1777" data-mp="y6scr3sp0ccx">{rain.note}</span>
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>

      <AnimatePresence data-id="element-1778" data-mp="ogfrf9d20f5q">
        {showDetails && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.3
      }} className="pointer-events-auto flex flex-wrap items-center justify-end gap-x-5 gap-y-3" data-id="element-1779" data-mp="afwk64gexd4m">
            <HouseModeChip mode={stage.mode} active size="wall" data-id="element-1780" data-mp="3s0ey70pl8v6l" />
            <span className="hidden text-[22px] text-ink-soft md:inline" data-id="element-1781" data-mp="3n0shghajwwt1">{room?.name}</span>
            <ul className="flex items-center gap-2" aria-label="Verbindingen" data-id="element-1782" data-mp="4unnnpanbzsf">
              {(Object.entries(meta.connections) as [string, boolean][]).map(([name, ok]) => <li key={name} className="flex items-center" data-id="element-1783" data-mp="erincbino68a">
                  <span className={`h-2 w-2 rounded-full ${ok ? 'bg-accent/80' : 'bg-amber'}`} aria-hidden data-id="element-1784" data-mp="eppbmht6qvtf" />
                  <span className="sr-only" data-id="element-1785" data-mp="ohvdqkd5itmc">{`${name}: ${ok ? 'verbonden' : 'weg'}`}</span>
                </li>)}
            </ul>
            {down.length > 0 && <span className="text-[22px] text-amber" data-id="element-1786" data-mp="jjad4jtzm7xd">{down.map(([n]) => n).join(', ')} weg</span>}
            <button type="button" onClick={() => setSoundOn(!soundOn)} aria-label={soundOn ? 'Geluid en stem uit' : 'Geluid en stem aan'} className="grid h-12 w-12 place-items-center rounded-full text-ink-muted transition-colors duration-150 hover:bg-white/[0.06] hover:text-ink" data-id="element-1787" data-mp="xj2xzyiu1txf">
              {soundOn ? <Volume2Icon className="h-6 w-6" data-id="element-1788" data-mp="2woyo5w4bq3cb" /> : <VolumeXIcon className="h-6 w-6" data-id="element-1789" data-mp="2wq08kr78c26p" />}
            </button>
          </motion.div>}
      </AnimatePresence>
    </header>;
}